import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TbBook } from "react-icons/tb";
import { useApi } from "src/hooks/useApi";
import { ChapterTabs, type OnboardingChapterDTO } from "./ChapterTabs";
import { StepRow } from "./StepRow";

interface OnboardingProgressResponse {
    overallPercent: number;
    totalSteps: number;
    totalDone: number;
    chapters: OnboardingChapterDTO[];
}

// Maps each onboarding chapter to the DocsPage section it's explained in — presentation-only
// routing, so it lives here rather than in the backend chapter config.
const CHAPTER_DOCS_SECTION: Record<string, string> = {
    patients: "onboarding",
    firstInvoice: "createInvoice",
    layout: "layout",
    bank: "bank",
    invoiceSettings: "invoices",
    dunning: "dunning",
    goa: "goa",
    goaCheck: "createInvoice",
    tagsGroups: "tagsGroups",
    consent: "onboarding",
    team: "team",
};

/** Progress bar + single-open chapter tabs — shared by the AVV wizard's step 3 and the standalone GettingStartedPopup. */
export function OnboardingChecklist({ onNavigate }: { onNavigate?: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // refreshTrigger=1 bypasses the response cache so reopening the popup always reflects
    // whatever settings/tags/consents changed since it was last opened.
    const { data, loading } = useApi<OnboardingProgressResponse>("/onboarding/progress", 1);
    const [activeId, setActiveId] = useState<string | null>(null);

    useEffect(() => {
        if (!data || activeId) return;
        const firstIncomplete = data.chapters.find((c) => c.doneCount < c.totalCount);
        setActiveId((firstIncomplete ?? data.chapters[0])?.id ?? null);
    }, [data, activeId]);

    if (!data) {
        return loading ? (
            <div className="onboarding-modal__checklist-loading">{t("onboarding.loadingProgress")}</div>
        ) : null;
    }

    const activeChapter = data.chapters.find((c) => c.id === activeId) ?? data.chapters[0];

    return (
        <div className="onboarding-modal__checklist">
            <div className="onboarding-modal__progress">
                <div className="onboarding-modal__progress-bar">
                    <div className="onboarding-modal__progress-fill" style={{ width: `${data.overallPercent}%` }} />
                </div>
                <span className="onboarding-modal__progress-label">
                    {t("onboarding.percentCompleted", { percent: data.overallPercent })}
                </span>
            </div>
            <div className="onboarding-modal__tabs-layout">
                <ChapterTabs chapters={data.chapters} activeId={activeChapter.id} onSelect={setActiveId} />
                <div className="onboarding-modal__tabs-content">
                    <div className="onboarding-modal__tab-panel" key={activeChapter.id}>
                        <div className="onboarding-modal__tab-panel-steps">
                            {activeChapter.steps.map((step) => (
                                <StepRow key={step.id} step={step} onNavigate={onNavigate} />
                            ))}
                        </div>
                        <button
                            type="button"
                            className="onboarding-modal__panel-docs"
                            onClick={() => {
                                onNavigate?.();
                                navigate(`/docs?section=${CHAPTER_DOCS_SECTION[activeChapter.id] ?? "quickstart"}`);
                            }}
                        >
                            <TbBook /> {t("onboarding.viewDocs", { chapter: t(activeChapter.titleKey) })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
